import React from 'react'
import { Box, IconButton } from '@material-ui/core'
import { Edit as EditIcon, Delete as DeleteIcon } from '@material-ui/icons'
import { useMutation } from '@apollo/react-hooks'
import moment from 'moment'

import { DATE_TIME_FORMAT } from '../../core/constants'
import { ICorporation } from '../../graphql/types/businesses'
import { GET_CORPORATIONS_BY_ADMIN, DELETE_CORPORATION } from '../../graphql/queries/businesses'
import {
  CORPORATIONS_ROWS_PER_PAGE,
  IBusinessCorporationsFilter,
  useCorporations,
} from '../../graphql/hooks/businesses'
import Pagination from '../../components/Data/pagination/Pagination'
import { PageHeader, Content } from '../../components/Layout'
import { EmptyDataMessage, DataTable, IDataTableColumn } from '../../components/Data'
import { useQueryParams } from '../../utils'
import { CreateFab, Loader, useConfirm } from '../../components/UI'
import { AddCorporateModal, CorporatesFilter, EditCorporateModal } from './components'
import { BusinessesCountColumn, CountryColumn, TokenColumn } from './columns'

const Corporates: React.FC = () => {
  const confirm = useConfirm()
  const [, setQuery] = useQueryParams()

  const [addModalVisible, setAddModalVisible] = React.useState<boolean>(false)
  const [editCorporation, setEditCorporation] = React.useState<ICorporation | null>(null)
  const [filter, setFilter] = React.useState<IBusinessCorporationsFilter>()

  const { corporations, total, loading, page, limit } = useCorporations(filter)

  const [deleteCorporation] = useMutation(DELETE_CORPORATION, {
    refetchQueries: [
      {
        query: GET_CORPORATIONS_BY_ADMIN,
        variables: {
          pagination: { offset: 0, limit: CORPORATIONS_ROWS_PER_PAGE[0] },
        },
      },
    ],
  })

  const handleEdit = (item: ICorporation) => () => {
    setEditCorporation(item)
  }

  const handleDelete = (item: ICorporation) => async () => {
    try {
      await confirm({ message: `Вы уверены, что хотите удалить корпорацию "${item.corporationName}"?` })
      await deleteCorporation({ variables: { id: item.id } })
    } catch {
      return
    }
  }

  const handleChangePerPage = (perPage: number) => {
    if (perPage !== limit) {
      setQuery({ page: 1, limit: perPage })
    }
  }

  const handleChangePage = (p: number) => {
    setQuery({ page: p + 1 })
  }

  const toggleModal = () => {
    setAddModalVisible(!addModalVisible)
  }

  const resetEditCorporation = () => {
    setEditCorporation(null)
  }

  const columns: IDataTableColumn<ICorporation>[] = [
    { label: 'ID', accessor: 'id', style: { width: 70 } },
    { label: 'Название', accessor: 'corporationName' },
    { label: 'Корпоративный код', accessor: item => <TokenColumn item={item} /> },
    { label: 'Тип', accessor: item => item.corporationTypes?.map(t => t.name).join(', ') ?? '-' },
    { label: 'Страна', accessor: item => <CountryColumn item={item} /> },
    { label: 'Кол-во партнеров', accessor: item => <BusinessesCountColumn item={item} /> },
    { label: 'Кол-во пользователей', accessor: 'corpUsersCount' },
    { label: 'Дата создания', accessor: item => moment(item.createdAt).format(DATE_TIME_FORMAT) },
    {
      label: '',
      accessor: item => (
        <Box display='flex' justifyContent='flex-end'>
          <IconButton size='small' onClick={handleEdit(item)}>
            <EditIcon />
          </IconButton>
          <IconButton size='small' onClick={handleDelete(item)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <div>
      <PageHeader title='Корпорации' right={<CorporatesFilter onChangeFilter={filter => setFilter(filter)} />} />
      <Box>
        {loading && <Loader />}
        {corporations.length === 0 && !loading ? (
          <EmptyDataMessage />
        ) : (
          <>
            <Content noGutter>
              <DataTable data={corporations} columns={columns} />
              <Box mt={3}>
                <Pagination
                  rowsPerPage={limit}
                  rowsPerPageOptions={CORPORATIONS_ROWS_PER_PAGE}
                  onChangeRowsPerPage={handleChangePerPage}
                  onChangePage={handleChangePage}
                  page={page - 1}
                  count={total}
                />
              </Box>
            </Content>
          </>
        )}
      </Box>
      <CreateFab onClick={toggleModal} />

      {addModalVisible && <AddCorporateModal open={addModalVisible} onCancel={toggleModal} />}
      {editCorporation && (
        <EditCorporateModal open={!!editCorporation} corporation={editCorporation} onCancel={resetEditCorporation} />
      )}
    </div>
  )
}

export default Corporates
